/**
 * Module #36 — Reports. The custom report builder + viewer.
 *
 * Pick a ready-made report (preset) or build your own: add columns, set their
 * ORDER (drag, or ↑/↓ — arrows are the keyboard/touch path), optionally
 * summarize/group + filter, then Run. The selected-columns order IS the output
 * column order (and the CSV order). Backed by the IE report engine
 * (allowlist-only, tenant-scoped server-side).
 *
 * Auth: inherits the /admin ProtectedRoute (tenant_owner / tenant_staff /
 * super_admin); the IE additionally gates the report endpoints to staff.
 */

import { useMemo, useState } from 'react';
import { Badge, Button } from '@auctisync/ui';
import {
  useReports,
  type Aggregation,
  type FilterOp,
  type ReportFieldMeta,
  type ReportPreset,
  type RunResult,
} from '../../hooks/useReports';
import {
  buildSpec,
  specToBuilderState,
  reorder,
  dropIndex,
  withColumnAdded,
  withColumnRemoved,
  omitKey,
  safeString,
  MONEY_TYPES,
  NUMERIC_TYPES,
  type FilterRow,
} from './reportBuilder';

const FILTER_OPS: { value: FilterOp; label: string; noValue?: boolean }[] = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'not equals' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'in (comma-sep)' },
  { value: 'between', label: 'between (a,b)' },
  { value: 'is_null', label: 'is empty', noValue: true },
  { value: 'is_not_null', label: 'is not empty', noValue: true },
];

function formatCell(value: unknown, type: string): string {
  if (value === null || value === undefined) return '';
  if (MONEY_TYPES.has(type)) {
    const n = Number(value);
    return Number.isFinite(n)
      ? `$${(n / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '';
  }
  if (type === 'bps') {
    const n = Number(value);
    return Number.isFinite(n) ? `${(n / 100).toFixed(2)}%` : '';
  }
  if (type === 'ratio') {
    const n = Number(value);
    return Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : '';
  }
  if (type === 'bool') return value === true ? 'Yes' : value === false ? 'No' : safeString(value);
  if (type === 'timestamp' || type === 'date') {
    const t = Date.parse(safeString(value));
    return Number.isNaN(t) ? safeString(value) : new Date(t).toLocaleString();
  }
  return safeString(value);
}

export function AdminReports() {
  const { datasets, presets, status, runReport, exportCsv } = useReports();

  const [presetKey, setPresetKey] = useState<string>('');
  const [datasetKey, setDatasetKey] = useState<string>('');
  // Ordered list of selected column keys — the array order is the report's
  // output column order (1:1 with the SQL SELECT list the IE compiler emits).
  const [included, setIncluded] = useState<string[]>([]);
  const [summarize, setSummarize] = useState(false);
  const [grouped, setGrouped] = useState<Set<string>>(new Set());
  const [aggs, setAggs] = useState<Record<string, Aggregation>>({});
  const [filterRows, setFilterRows] = useState<FilterRow[]>([]);

  // Native drag state (mouse enhancement; ↑/↓ buttons are the primary path).
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [moveMsg, setMoveMsg] = useState(''); // polite aria-live announcement
  const [moveSeq, setMoveSeq] = useState(0); // remounts the live region so SRs re-announce identical text

  const [result, setResult] = useState<RunResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  const dataset = useMemo(
    () => datasets.find((d) => d.key === datasetKey) ?? null,
    [datasets, datasetKey],
  );
  const selectedPreset = useMemo(
    () => presets.find((p) => p.key === presetKey) ?? null,
    [presets, presetKey],
  );
  const fieldByKey = useMemo(
    () => new Map((dataset?.fields ?? []).map((f) => [f.key, f])),
    [dataset],
  );
  const availableFields = useMemo(
    () => (dataset?.fields ?? []).filter((f) => !included.includes(f.key)),
    [dataset, included],
  );

  function selectDataset(key: string): void {
    setPresetKey(''); // changing the dataset detaches any loaded preset
    setDatasetKey(key);
    setIncluded([]);
    setSummarize(false);
    setGrouped(new Set());
    setAggs({});
    setFilterRows([]);
    setResult(null);
    setRunError(null);
    setMoveMsg('');
  }

  function loadPreset(preset: ReportPreset): void {
    const st = specToBuilderState(preset.spec);
    setPresetKey(preset.key);
    setDatasetKey(preset.spec.dataset);
    setIncluded(st.included);
    setSummarize(st.summarize);
    setGrouped(st.grouped);
    setAggs(st.aggs);
    setFilterRows(st.filterRows);
    setResult(null);
    setRunError(null);
    setMoveMsg('');
  }

  function onPickPreset(key: string): void {
    if (key === '') {
      setPresetKey(''); // "Custom" — keep the builder, just detach the label
      return;
    }
    const p = presets.find((x) => x.key === key);
    if (p !== undefined) loadPreset(p);
  }

  function addColumn(key: string): void {
    setIncluded((prev) => withColumnAdded(prev, key));
  }

  function removeColumn(key: string): void {
    setIncluded((prev) => withColumnRemoved(prev, key));
    // Keep dependent state honest when a column leaves the report.
    setAggs((prev) => omitKey(prev, key));
    setGrouped((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }

  function moveColumn(from: number, to: number): void {
    const next = reorder(included, from, to);
    if (next === included) return; // no-op (boundary / same index)
    setIncluded(next);
    const movedKey = next[to];
    const label =
      (movedKey !== undefined ? fieldByKey.get(movedKey)?.label : undefined) ?? 'column';
    setMoveMsg(`Moved ${label} to position ${to + 1} of ${next.length}`);
    setMoveSeq((n) => n + 1);
  }

  function toggleGroup(key: string): void {
    setGrouped((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function onDropRow(targetIdx: number): void {
    // The drop indicator promises "insert before this row"; translate that to
    // the move-to index reorder expects (downward drags shift the target left).
    if (dragIdx !== null && dragIdx !== targetIdx)
      moveColumn(dragIdx, dropIndex(dragIdx, targetIdx));
    setDragIdx(null);
    setOverIdx(null);
  }

  async function onRun(): Promise<void> {
    if (dataset === null || included.length === 0) return;
    setBusy(true);
    setRunError(null);
    const spec = buildSpec(dataset, included, summarize, grouped, aggs, filterRows);
    const out = await runReport(spec);
    setBusy(false);
    if (out.ok) setResult(out.result);
    else {
      setResult(null);
      setRunError(out.detail);
    }
  }

  async function onExport(): Promise<void> {
    if (dataset === null || included.length === 0) return;
    const spec = buildSpec(dataset, included, summarize, grouped, aggs, filterRows);
    const out = await exportCsv(spec);
    if (!out.ok) setRunError(out.detail ?? 'Export failed.');
  }

  return (
    <div style={{ maxWidth: '72rem' }}>
      <header style={{ marginBottom: '1.25rem' }}>
        <h1 style={h1Style}>Reports</h1>
        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.95rem' }}>
          Start from a ready-made report or build your own — add columns, set their order, summarize
          or filter, then run or export to CSV.
        </p>
      </header>

      {status.kind === 'loading' && <p style={{ color: 'var(--text-muted)' }}>Loading datasets…</p>}
      {status.kind === 'error' && (
        <div role="alert" style={alertStyle}>
          Couldn't load report datasets: {status.detail}
        </div>
      )}

      {status.kind === 'idle' && datasets.length > 0 && (
        <div style={{ display: 'grid', gap: '1.25rem' }}>
          {/* Report picker (presets) */}
          {presets.length > 0 && (
            <section style={cardStyle}>
              <label style={labelStyle} htmlFor="preset">
                Report
              </label>
              <select
                id="preset"
                value={presetKey}
                onChange={(e) => onPickPreset(e.target.value)}
                style={selectStyle}
              >
                <option value="">Custom — build from scratch</option>
                {datasets.map((d) => {
                  const group = presets.filter((p) => p.dataset === d.key);
                  if (group.length === 0) return null;
                  return (
                    <optgroup key={d.key} label={d.label}>
                      {group.map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.label}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
              {selectedPreset !== null && (
                <p
                  style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}
                >
                  {selectedPreset.description} Loaded into the builder — reorder or edit anything
                  below before you run.
                </p>
              )}
              {selectedPreset?.needs != null && selectedPreset.needs.length > 0 && (
                <p style={needsNoteStyle}>
                  This report is ready, but shows data once you have {selectedPreset.needs}.
                </p>
              )}
            </section>
          )}

          {/* Dataset picker */}
          <section style={cardStyle}>
            <label style={labelStyle} htmlFor="dataset">
              Dataset
            </label>
            <select
              id="dataset"
              value={datasetKey}
              onChange={(e) => selectDataset(e.target.value)}
              style={selectStyle}
            >
              <option value="">Choose a dataset…</option>
              {datasets.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </select>
            {dataset !== null && (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
                {dataset.description}
              </p>
            )}
          </section>

          {dataset !== null && (
            <>
              {/* Columns — add from the left, order on the right */}
              <section style={cardStyle}>
                <div style={rowBetween}>
                  <h2 style={h2Style}>Columns</h2>
                  <label
                    style={{ ...labelStyle, display: 'inline-flex', gap: '0.4rem', margin: 0 }}
                  >
                    <input
                      type="checkbox"
                      checked={summarize}
                      onChange={(e) => setSummarize(e.target.checked)}
                    />
                    Summarize (group + total)
                  </label>
                </div>

                <div style={twoPaneStyle}>
                  {/* Available fields */}
                  <div style={paneStyle}>
                    <p style={paneHeadStyle}>Available fields</p>
                    {availableFields.length === 0 ? (
                      <p style={paneEmptyStyle}>All fields added.</p>
                    ) : (
                      <div style={{ display: 'grid', gap: '0.2rem' }}>
                        {availableFields.map((f) => (
                          <button
                            key={f.key}
                            type="button"
                            onClick={() => addColumn(f.key)}
                            style={availRowStyle}
                            aria-label={`Add ${f.label}`}
                          >
                            <span style={{ flex: 1, textAlign: 'left' }}>{f.label}</span>
                            {f.measure && (
                              <Badge variant="outline" style={{ fontSize: '0.6rem' }}>
                                measure
                              </Badge>
                            )}
                            <span aria-hidden style={{ color: 'var(--text-muted)' }}>
                              + Add
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selected columns — ordered, reorderable */}
                  <div style={paneStyle}>
                    <p style={paneHeadStyle}>
                      Selected columns{' '}
                      <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                        — drag, or use ↑/↓, to set the report order
                      </span>
                    </p>
                    {included.length === 0 ? (
                      <p style={paneEmptyStyle}>No columns yet — add fields from the left.</p>
                    ) : (
                      <div role="list" aria-label="Selected columns, in report order">
                        {included.map((key, i) => {
                          const f = fieldByKey.get(key);
                          if (f === undefined) return null;
                          return (
                            <SelectedColumnRow
                              key={key}
                              field={f}
                              index={i}
                              total={included.length}
                              summarize={summarize}
                              groupingActive={summarize && grouped.size > 0}
                              grouped={grouped.has(key)}
                              agg={aggs[key]}
                              isDragging={dragIdx === i}
                              isOver={overIdx === i && dragIdx !== null && dragIdx !== i}
                              onMoveUp={() => moveColumn(i, i - 1)}
                              onMoveDown={() => moveColumn(i, i + 1)}
                              onRemove={() => removeColumn(key)}
                              onToggleGroup={() => toggleGroup(key)}
                              onAgg={(a) => setAggs((prev) => ({ ...prev, [key]: a }))}
                              onDragStart={() => setDragIdx(i)}
                              onDragOver={() => {
                                if (overIdx !== i) setOverIdx(i);
                              }}
                              onDragLeave={() => setOverIdx((cur) => (cur === i ? null : cur))}
                              onDrop={() => onDropRow(i)}
                              onDragEnd={() => {
                                setDragIdx(null);
                                setOverIdx(null);
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {/* Polite SR announcement for keyboard reorder. `key` remounts the
                    node so an identical message is still re-announced. */}
                <div key={moveSeq} aria-live="polite" style={srOnlyStyle}>
                  {moveMsg}
                </div>
              </section>

              {/* Filters */}
              <section style={cardStyle}>
                <div style={rowBetween}>
                  <h2 style={h2Style}>Filters</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFilterRows((prev) => [...prev, { field: '', op: 'eq', value: '' }])
                    }
                  >
                    + Add filter
                  </Button>
                </div>
                {filterRows.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>
                    No filters — the report includes all rows.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    {filterRows.map((row, i) => (
                      <FilterRowEditor
                        key={i}
                        row={row}
                        fields={dataset.fields.filter((f) => f.filterable !== false)}
                        onChange={(next) =>
                          setFilterRows((prev) => prev.map((r, j) => (j === i ? next : r)))
                        }
                        onRemove={() => setFilterRows((prev) => prev.filter((_, j) => j !== i))}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* Run / export */}
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <Button disabled={busy || included.length === 0} onClick={() => void onRun()}>
                  {busy ? 'Running…' : 'Run report'}
                </Button>
                <Button
                  variant="outline"
                  disabled={busy || included.length === 0}
                  onClick={() => void onExport()}
                >
                  Export CSV
                </Button>
                {included.length === 0 && (
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Add at least one column.
                  </span>
                )}
              </div>

              {runError !== null && (
                <div role="alert" style={alertStyle}>
                  {runError}
                </div>
              )}

              {result !== null && <ResultTable result={result} />}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SelectedColumnRow({
  field,
  index,
  total,
  summarize,
  groupingActive,
  grouped,
  agg,
  isDragging,
  isOver,
  onMoveUp,
  onMoveDown,
  onRemove,
  onToggleGroup,
  onAgg,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: {
  field: ReportFieldMeta;
  index: number;
  total: number;
  summarize: boolean;
  groupingActive: boolean;
  grouped: boolean;
  agg: Aggregation | undefined;
  isDragging: boolean;
  isOver: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onToggleGroup: () => void;
  onAgg: (a: Aggregation) => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const atTop = index === 0;
  const atBottom = index === total - 1;
  // In summarize mode, an ungrouped non-measure dimension is dropped from the
  // output by the compiler — warn so the empty column isn't a surprise.
  const willDrop = groupingActive && !grouped && !field.measure;
  return (
    <div
      role="listitem"
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver();
      }}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        ...selectedRowStyle,
        opacity: isDragging ? 0.5 : 1,
        borderTop: isOver ? '2px solid var(--ring, #6366f1)' : '2px solid transparent',
      }}
    >
      <span
        draggable
        onDragStart={(e) => {
          // Firefox aborts the drag unless dragstart sets some transfer data.
          e.dataTransfer.setData('text/plain', String(index));
          e.dataTransfer.effectAllowed = 'move';
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        aria-hidden
        title="Drag to reorder"
        style={{
          cursor: 'grab',
          color: 'var(--text-muted)',
          userSelect: 'none',
          padding: '0 0.1rem',
        }}
      >
        ⠿
      </span>
      <span style={{ flex: 1, fontSize: '0.9rem', color: 'var(--text)' }}>{field.label}</span>
      {field.measure && (
        <Badge variant="outline" style={{ fontSize: '0.6rem' }}>
          measure
        </Badge>
      )}
      {willDrop && (
        <Badge variant="outline" style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>
          won&rsquo;t show — check “group”
        </Badge>
      )}

      {summarize && field.groupable && (
        <label
          style={{
            display: 'inline-flex',
            gap: '0.3rem',
            alignItems: 'center',
            fontSize: '0.8rem',
          }}
        >
          <input type="checkbox" checked={grouped} onChange={onToggleGroup} />
          group
        </label>
      )}
      {summarize && field.measure && !grouped && (
        <select
          value={agg ?? field.aggregations?.[0] ?? 'sum'}
          onChange={(e) => onAgg(e.target.value as Aggregation)}
          aria-label={`Aggregate for ${field.label}`}
          style={{ ...selectStyle, width: 'auto', padding: '0.2rem 0.4rem', fontSize: '0.8rem' }}
        >
          {(field.aggregations ?? ['sum', 'avg', 'min', 'max', 'count']).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      )}

      {/* Arrows use aria-disabled (not disabled) so a boundary press keeps focus
          instead of dropping it to <body> — the move is already a no-op. */}
      <div style={{ display: 'inline-flex', gap: '0.1rem' }}>
        <Button
          variant="ghost"
          size="sm"
          aria-disabled={atTop}
          onClick={() => {
            if (!atTop) onMoveUp();
          }}
          aria-label={`Move ${field.label} up`}
          style={{ opacity: atTop ? 0.4 : undefined }}
        >
          ↑
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-disabled={atBottom}
          onClick={() => {
            if (!atBottom) onMoveDown();
          }}
          aria-label={`Move ${field.label} down`}
          style={{ opacity: atBottom ? 0.4 : undefined }}
        >
          ↓
        </Button>
        <Button variant="ghost" size="sm" onClick={onRemove} aria-label={`Remove ${field.label}`}>
          ✕
        </Button>
      </div>
    </div>
  );
}

function FilterRowEditor({
  row,
  fields,
  onChange,
  onRemove,
}: {
  row: FilterRow;
  fields: ReportFieldMeta[];
  onChange: (next: FilterRow) => void;
  onRemove: () => void;
}) {
  const op = FILTER_OPS.find((o) => o.value === row.op);
  return (
    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <select
        value={row.field}
        onChange={(e) => onChange({ ...row, field: e.target.value })}
        style={{ ...selectStyle, width: 'auto', minWidth: '11rem' }}
      >
        <option value="">Field…</option>
        {fields.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </select>
      <select
        value={row.op}
        onChange={(e) => onChange({ ...row, op: e.target.value as FilterOp })}
        style={{ ...selectStyle, width: 'auto' }}
      >
        {FILTER_OPS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {op?.noValue !== true && (
        <input
          value={row.value}
          onChange={(e) => onChange({ ...row, value: e.target.value })}
          placeholder="value"
          style={inputStyle}
        />
      )}
      <Button variant="ghost" size="sm" onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
}

function ResultTable({ result }: { result: RunResult }) {
  return (
    <section>
      <div style={{ ...rowBetween, marginBottom: '0.5rem' }}>
        <h2 style={h2Style}>
          Results{' '}
          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({result.rowCount})</span>
        </h2>
        {result.truncated && <Badge variant="outline">capped — refine to see more</Badge>}
      </div>
      {result.rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No rows match.</p>
      ) : (
        <div
          style={{
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-card, 0.5rem)',
            overflow: 'auto',
            background: 'var(--surface)',
            maxHeight: '32rem',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: 'var(--surface-elev)' }}>
                {result.columns.map((c) => (
                  <th key={c.key} style={thStyle}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--admin-hairline, var(--border))' }}>
                  {result.columns.map((c) => {
                    const numeric = NUMERIC_TYPES.has(c.type);
                    return (
                      <td
                        key={c.key}
                        style={{
                          ...tdStyle,
                          textAlign: numeric ? 'right' : 'left',
                          whiteSpace: 'nowrap',
                          fontVariantNumeric: numeric ? 'tabular-nums' : 'normal',
                        }}
                      >
                        {formatCell(row[c.key], c.type)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const h1Style: React.CSSProperties = {
  fontFamily: 'var(--font-heading-active)',
  fontSize: '1.75rem',
  fontWeight: 700,
  color: 'var(--text)',
  margin: '0 0 0.3rem',
};
const h2Style: React.CSSProperties = {
  fontSize: '1.05rem',
  fontWeight: 600,
  color: 'var(--text)',
  margin: 0,
};
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface)',
  padding: '1rem',
};
const rowBetween: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '0.75rem',
  gap: '0.5rem',
};
const twoPaneStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))',
  gap: '1rem',
  alignItems: 'start',
};
const paneStyle: React.CSSProperties = {
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface-elev)',
  padding: '0.6rem',
  minWidth: 0,
};
const paneHeadStyle: React.CSSProperties = {
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'var(--text)',
  margin: '0 0 0.5rem',
};
const paneEmptyStyle: React.CSSProperties = {
  fontSize: '0.82rem',
  color: 'var(--text-muted)',
  margin: 0,
  padding: '0.4rem 0.2rem',
};
const availRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  width: '100%',
  padding: '0.4rem 0.5rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.88rem',
  cursor: 'pointer',
};
const selectedRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.35rem 0.4rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderTop: '2px solid transparent',
  marginBottom: '0.25rem',
};
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '0.35rem',
};
const selectStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '24rem',
  padding: '0.45rem 0.6rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.9rem',
};
const inputStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.85rem',
  minWidth: '9rem',
};
const alertStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  fontSize: '0.9rem',
};
const needsNoteStyle: React.CSSProperties = {
  margin: '0.5rem 0 0',
  padding: '0.4rem 0.6rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-elev)',
  border: '1px solid var(--admin-hairline, var(--border))',
  color: 'var(--text-muted)',
  fontSize: '0.82rem',
};
const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.75rem',
  fontSize: '0.68rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  background: 'var(--surface-elev)',
};
const tdStyle: React.CSSProperties = { padding: '0.5rem 0.75rem', color: 'var(--text)' };
